import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const CartContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stückpreis eines Warenkorb-Artikels — berücksichtigt "ohne Betreuung" (eigene Staffel/Festpreis), Staffelpreise und (Phase 133.86) ausgewählte Preisvarianten.
export const getCartItemUnitPrice = (item) => {
  const p = item?.product || {};
  const d = Math.max(1, parseInt(item?.duration || 1, 10));
  const useWithout = p.requires_supervision && item?.withSupervision === false && p.price_without_supervision != null;
  // Phase 133.86 — Preisvariante überschreibt Basis-Preise (nicht aber Staffel/ohne-Betreuung-Zweig).
  // Varianten gelten als Alternativen zur Standard-Ausführung und besitzen eigene 1-Tag/3-Tage-Preise.
  const po = item?.priceOption;
  const tiersRaw = useWithout
    ? (Array.isArray(p.tiered_prices_without_supervision) ? p.tiered_prices_without_supervision : [])
    : (Array.isArray(p.tiered_prices) ? p.tiered_prices : []);
  const tieredOn = p.tiered_pricing_enabled && tiersRaw.length > 0;
  const extraOn = p.extra_day_enabled && p.price_per_extra_day != null && p.price_per_extra_day !== '';
  const extra = parseFloat(p.price_per_extra_day) || 0;
  if (tieredOn) {
    const tiers = tiersRaw
      .map((t) => ({ days: parseInt(t.days, 10), price: parseFloat(t.price) }))
      .filter((t) => t.days > 0 && !Number.isNaN(t.price))
      .sort((a, b) => a.days - b.days);
    if (tiers.length) {
      const exact = tiers.find((t) => t.days === d);
      if (exact) return exact.price;
      const lowers = tiers.filter((t) => t.days <= d);
      if (lowers.length) {
        const base = lowers[lowers.length - 1];
        return extraOn ? base.price + (d - base.days) * extra : base.price;
      }
      return tiers[0].price;
    }
  }
  if (useWithout) return Number(p.price_without_supervision) || 0;
  // Wenn eine Variante gewählt wurde, ihre Preise verwenden
  if (po && !useWithout) {
    if (p.single_price) return Number(po.price_1_day) || 0;
    return Number(d === 1 ? po.price_1_day : (po.price_3_days || po.price_1_day)) || 0;
  }
  if (p.single_price) return Number(p.price_1_day) || 0;
  return Number(d === 1 ? p.price_1_day : p.price_3_days) || 0;
};

const ensureCartSessionId = () => {
  let sid = localStorage.getItem('cartSessionId');
  if (!sid) {
    sid = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('cartSessionId', sid);
  }
  return sid;
};

const setGlobalHoldExpiry = (exp) => {
  try {
    if (exp) localStorage.setItem('cartHoldExpiresAt', exp);
    else localStorage.removeItem('cartHoldExpiresAt');
    window.dispatchEvent(new Event('cartHoldUpdated'));
  } catch { /* noop */ }
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [accessories, setAccessories] = useState([]); // [{ accessory, quantity }]
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('cart');
    if (saved) {
      try { setCart(JSON.parse(saved)); } catch (_) {}
    }
    const savedAcc = localStorage.getItem('cart_accessories');
    if (savedAcc) {
      try { setAccessories(JSON.parse(savedAcc)); } catch (_) {}
    }
    setHydrated(true);
  }, []);

  // Phase 128.21 — Verkaufte Produkte automatisch aus dem Warenkorb entfernen.
  // Wird einmalig nach Hydration ausgeführt; iteriert alle Produkte via GET
  // /products/{id}: 404 (sold=True) → aus Warenkorb entfernen, User informieren.
  useEffect(() => {
    if (!hydrated || cart.length === 0) return;
    let cancelled = false;
    (async () => {
      const drop = [];
      for (const it of cart) {
        const pid = it?.product?.id;
        if (!pid) continue;
        try {
          await axios.get(`${API}/products/${pid}`);
        } catch (err) {
          if (err?.response?.status === 404) drop.push({ id: pid, name: it.product?.name || 'Produkt' });
        }
      }
      if (!cancelled && drop.length > 0) {
        setCart((cur) => cur.filter((it) => !drop.some((d) => d.id === it.product?.id)));
        // Toast fire-and-forget via dynamic import (sonner ist Frontend-only).
        try {
          const { toast } = await import('sonner');
          drop.forEach((d) => toast.error(`„${d.name}" ist nicht mehr verfügbar und wurde aus deinem Warenkorb entfernt.`));
        } catch (_) {}
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated]);

  useEffect(() => {
    if (!hydrated) return; // Avoid wiping localStorage with the initial empty state
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart, hydrated]);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem('cart_accessories', JSON.stringify(accessories));
  }, [accessories, hydrated]);

  // 30-Min-Reservierung: sobald Produkte (mit Datum) im Warenkorb liegen, wird die
  // Session-Reservierung serverseitig gesetzt und der globale Countdown eingeblendet.
  // WICHTIG: Bei einem UNVERÄNDERTEN Warenkorb (z.B. Kunde kommt am nächsten Tag
  // wieder) wird KEIN neuer Hold angelegt – der Countdown startet nicht neu und der
  // Buchungskalender wird nicht erneut blockiert. Ein neuer 30-Min-Zeitraum entsteht
  // nur, wenn sich Produkte/Termine tatsächlich ändern.
  useEffect(() => {
    if (!hydrated) return;
    const datedItems = cart.filter((it) => it.eventDate && it.product?.id && !it.product?.price_on_request);
    if (datedItems.length === 0) {
      const sid = localStorage.getItem('cartSessionId');
      if (sid) axios.delete(`${API}/cart-holds`, { params: { session_id: sid } }).catch(() => {});
      setGlobalHoldExpiry(null);
      localStorage.removeItem('cartHoldSignature');
      localStorage.removeItem('cartHoldExpiresAt');
      return;
    }
    const froms = datedItems.map((it) => it.eventDate).sort();
    const tos = datedItems.map((it) => it.eventDateTo || it.eventDate).sort();
    // Signatur = welche Produkte (+ Menge) an welchen Terminen reserviert sind.
    const signature = JSON.stringify(
      datedItems
        .map((it) => ({ p: it.product.id, q: it.quantity || 1, f: it.eventDate, t: it.eventDateTo || it.eventDate }))
        .sort((a, b) => (a.p + a.f).localeCompare(b.p + b.f))
    );
    const prevSignature = localStorage.getItem('cartHoldSignature');
    const prevExpiry = localStorage.getItem('cartHoldExpiresAt');
    if (signature === prevSignature) {
      // Unveränderter Warenkorb: bestehenden (evtl. abgelaufenen) Ablauf nur anzeigen,
      // keinen neuen Hold anlegen. Abgelaufen -> kein Countdown, keine Neublockierung.
      setGlobalHoldExpiry(prevExpiry && new Date(prevExpiry) > new Date() ? prevExpiry : null);
      return;
    }
    const sessionId = ensureCartSessionId();
    const t = setTimeout(() => {
      axios.post(`${API}/cart-holds`, {
        session_id: sessionId,
        date_from: froms[0],
        date_to: tos[tos.length - 1],
        items: datedItems.map((it) => ({ product_id: it.product.id, quantity: it.quantity || 1 })),
      })
        .then((res) => {
          const exp = res.data?.expires_at || null;
          setGlobalHoldExpiry(exp);
          localStorage.setItem('cartHoldSignature', signature);
        })
        .catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [cart, hydrated]);

  // --- Zubehör (Phase 77) ---
  const _clampAccQty = (accessory, qty) => {
    const max = Math.max(1, Math.min(10, Number(accessory.max_quantity) || 10));
    return Math.max(1, Math.min(max, Number(qty) || 1));
  };

  const addAccessory = (accessory, quantity = 1) => {
    const cleanQty = _clampAccQty(accessory, quantity);
    setAccessories((prev) => {
      const idx = prev.findIndex((a) => a.accessory.id === accessory.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { accessory, quantity: cleanQty };
        return updated;
      }
      return [...prev, { accessory, quantity: cleanQty }];
    });
  };

  const setAccessoryQuantity = (accessoryId, quantity) => {
    setAccessories((prev) => prev.map((a) =>
      a.accessory.id === accessoryId
        ? { ...a, quantity: _clampAccQty(a.accessory, quantity) }
        : a
    ));
  };

  const removeAccessory = (accessoryId) => {
    setAccessories((prev) => prev.filter((a) => a.accessory.id !== accessoryId));
  };

  // Phase 122S-fix — Hydration-Helper für den Admin-Booking-Editor: ersetzt
  // die Zubehör-Liste im Cart komplett (kein Merge, kein Persist-Konflikt mit
  // vorher gebuchtem Zubehör in einer anderen Buchung).
  const replaceAccessories = (list) => {
    setAccessories(Array.isArray(list) ? list : []);
  };

  // Phase 122S — kompletter Cart-Snapshot / Restore für Admin-Editor.
  // Verhindert, dass beim Öffnen einer bestehenden Buchung Items im
  // öffentlichen Warenkorb landen (localStorage-Leak).
  const snapshotCart = () => ({
    cart: JSON.parse(JSON.stringify(cart || [])),
    accessories: JSON.parse(JSON.stringify(accessories || [])),
  });
  const restoreCartSnapshot = (snap) => {
    if (!snap) return;
    setCart(Array.isArray(snap.cart) ? snap.cart : []);
    setAccessories(Array.isArray(snap.accessories) ? snap.accessories : []);
  };

  const getAccessoriesTotal = () =>
    accessories.reduce((sum, a) => sum + (Number(a.accessory.price) || 0) * a.quantity, 0);

  const addToCart = (product, duration, eventDate, eventDateTo = null, opts = {}) => {
    const { withSupervision, quantity = 1, funfood = null, deliveryAddon = null, setupAddon = null, priceOption = null, cleaningAddon = null } = opts;
    // Each product may only be in the cart once – regardless of duration / date / supervision.
    // If the same product is already present, simply update its parameters instead of
    // adding a second row. Quantity is only respected for products with `allow_quantity`.
    const allowQty = !!product.allow_quantity;
    const cleanQty = allowQty ? Math.max(1, Number(quantity) || 1) : 1;
    setCart((cart) => {
    const existingIndex = cart.findIndex((item) => item.product.id === product.id);
    if (existingIndex >= 0) {
      const updated = [...cart];
      updated[existingIndex] = {
        ...updated[existingIndex],
        duration,
        eventDate,
        eventDateTo: eventDateTo || eventDate,
        withSupervision: withSupervision ?? updated[existingIndex].withSupervision ?? true,
        quantity: cleanQty,
        funfood: funfood ?? updated[existingIndex].funfood ?? null,
        // Phase 133.82 — Zusatzleistungen pro Produkt (Lieferung / Auf-/Abbau).
        // Werden explizit übergeben → ersetzen den bisherigen Stand. Wenn der
        // Aufrufer `null` liefert (z.B. Schnell-Button „Jetzt anfragen"),
        // behalten wir die vorherige Auswahl.
        deliveryAddon: deliveryAddon ?? updated[existingIndex].deliveryAddon ?? null,
        setupAddon: setupAddon ?? updated[existingIndex].setupAddon ?? null,
        cleaningAddon: cleaningAddon ?? updated[existingIndex].cleaningAddon ?? null,
        // Phase 133.86 — Gewählte Preisvariante (null = Standard)
        priceOption: priceOption ?? updated[existingIndex].priceOption ?? null,
      };
      return updated;
    }
      return [...cart, {
        product,
        duration,
        eventDate,
        eventDateTo: eventDateTo || eventDate,
        quantity: cleanQty,
        withSupervision: withSupervision ?? true,
        funfood: funfood || null,
        deliveryAddon: deliveryAddon || null,
        setupAddon: setupAddon || null,
        cleaningAddon: cleaningAddon || null,
        priceOption: priceOption || null, // Phase 133.86
      }];
    });
  };

  // Phase 133.82 — Addons eines bestehenden Cart-Items aktualisieren (z.B.
  // Toggle in Warenkorb-Sheet). Partial-Merge: nur die gelieferten Keys werden
  // überschrieben; `null` löscht die jeweilige Addon-Auswahl.
  const updateItemAddons = (productId, patch) => {
    setCart((prev) => prev.map((it) => {
      if (it.product.id !== productId) return it;
      const next = { ...it };
      if (Object.prototype.hasOwnProperty.call(patch, 'deliveryAddon')) {
        next.deliveryAddon = patch.deliveryAddon;
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'setupAddon')) {
        next.setupAddon = patch.setupAddon;
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'cleaningAddon')) {
        next.cleaningAddon = patch.cleaningAddon;
      }
      return next;
    }));
  };

  // Phase 133.82 — Summe aller aktiven Zusatzleistungen eines Items (0 wenn keine).
  const getCartItemAddonsTotal = (item) => {
    if (!item) return 0;
    let sum = 0;
    if (item.deliveryAddon?.enabled && item.deliveryAddon.price > 0) {
      sum += Number(item.deliveryAddon.price) || 0;
    }
    if (item.setupAddon?.enabled && item.setupAddon.price > 0) {
      sum += (Number(item.setupAddon.price) || 0) * Math.max(1, item.quantity || 1);
    } else if (item.cleaningAddon?.enabled && item.cleaningAddon.price > 0) {
      sum += (Number(item.cleaningAddon.price) || 0) * Math.max(1, item.quantity || 1);
    }
    return sum;
  };

  const setItemQuantity = (productId, newQty) => {
    setCart((prev) => prev.map((it) => {
      if (it.product.id !== productId || !it.product.allow_quantity) return it;
      const max = it.product.stock_quantity ? Number(it.product.stock_quantity) : 99;
      const clamped = Math.max(1, Math.min(max, Number(newQty) || 1));
      return { ...it, quantity: clamped };
    }));
  };

  const removeFromCart = (productId, duration, eventDate) => {
    setCart(
      cart.filter(
        (item) =>
          !(
            item.product.id === productId &&
            item.duration === duration &&
            item.eventDate === eventDate
          )
      )
    );
  };

  // Entfernt ALLE Cart-Einträge mit gegebener Produkt-ID, unabhängig von
  // duration/eventDate — wird von der Mietanfrage aufgerufen, wenn der Kunde
  // dort ein Produkt entfernt.
  const removeAllByProduct = (productId) => {
    setCart(cart.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId, duration, eventDate, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId, duration, eventDate);
      return;
    }
    setCart(
      cart.map((item) =>
        item.product.id === productId &&
        item.duration === duration &&
        item.eventDate === eventDate
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    setAccessories([]);
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => {
      if (item.product?.price_on_request) return sum;
      const rentPart = getCartItemUnitPrice(item) * item.quantity;
      // Phase 133.82 — Addons (Lieferung/Auf-Abbau) in Gesamtsumme einrechnen.
      return sum + rentPart + getCartItemAddonsTotal(item);
    }, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        removeAllByProduct,
        updateQuantity,
        setItemQuantity,
        clearCart,
        getTotal,
        updateItemAddons,
        getCartItemAddonsTotal,
        accessories,
        addAccessory,
        setAccessoryQuantity,
        removeAccessory,
        replaceAccessories,
        snapshotCart,
        restoreCartSnapshot,
        getAccessoriesTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
